import * as React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import "./styles.css";
import TimeList from "./time-list";
import Calendar from "./calendar";
import { computeTimeRange } from "./utils";
import {
  DateType,
  TimeType,
  getFormattedDateTime,
  getFormattedTime,
  Moment
} from "../../model/utils";
import { fieldChanged, focusOn } from "../../store/actions";
import useCloseOnBlur from "../use-close-on-blur";
import { useElementDOMProperty } from "../../hooks";

const mapDispatchToProps = dispatch => ({
  fieldChanged: fieldName => dispatch(fieldChanged(fieldName)),
  focusOn: fieldName => dispatch(focusOn(fieldName))
});

const SelectDateRange = ({
  fieldName,
  fieldChanged,
  focusOn,
  focusedField,
  label,
  timeData,
  isDateRange,
  date,
  setDate,
  time: arrayTime,
  setTime: setArrayTime,
  allowedDateRanges,
  error
}) => {
  const time = arrayTime && getFormattedTime(arrayTime);
  const setTime = React.useCallback(
    newTime =>
      setArrayTime &&
      setArrayTime(
        newTime ? newTime.split(":").map(x => parseInt(x, 10)) : newTime
      ),
    [setArrayTime]
  );

  const [opened, setOpened] = React.useState(false);

  const onFocus = useCloseOnBlur(
    focusOn,
    fieldName,
    focusedField,
    opened,
    setOpened
  );

  const [localDate, setLocalDate] = React.useState(date);
  const [localTime, setLocalTime] = React.useState(time);

  const usedDate = opened ? localDate : date;
  const setUsedDate = opened ? setLocalDate : setDate;
  const usedTime = opened ? localTime : time;
  const setUsedTime = opened ? setLocalTime : setTime;

  const timeSelector = getTimeSelector(timeData, usedTime, setUsedTime);

  const toggleDateSelector = React.useCallback(() => {
    onFocus();
    if (!opened) {
      setLocalDate(date);
      setLocalTime(time);
    }
    setOpened(!opened);
  }, [date, time, opened, onFocus]);

  const confirm = () => {
    setDate(localDate);
    if (setTime) {
      setTime(localTime);
    }
    setOpened(false);
    if (fieldName) {
      fieldChanged(fieldName);
    }
  };

  const {
    domProperty: errorTextHeight,
    elementRef: errorTextRef
  } = useElementDOMProperty(
    { getDOMProperty: el => el.offsetHeight, defaultValue: 0 },
    [error]
  );

  return (
    <div
      styleName="date-and-time-container"
      id={`input-field-name-${fieldName}`}
    >
      <div styleName="label">{label}</div>
      <div styleName="value" onClick={toggleDateSelector}>
        {!date
          ? getDefaultLabel(isDateRange, timeData)
          : getFormattedDateTime(date, time)}
      </div>
      {opened && (
        <div styleName="popover">
          <div styleName="date-selector-container">
            <div styleName="react-calendar-container">
              <Calendar
                setDate={setUsedDate}
                date={usedDate}
                isDateRange={isDateRange}
                allowedDateRanges={allowedDateRanges}
              />
            </div>
            {usedDate && (
              <>
                {timeSelector}
                {(!timeData || usedTime) && (
                  <div styleName="confirm-button-container">
                    <button
                      type="button"
                      styleName="confirm-button"
                      onClick={confirm}
                    >
                      Conferma
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <div styleName="error" style={{ height: errorTextHeight }}>
        <div ref={errorTextRef}>{error}</div>
      </div>
    </div>
  );
};

const getTimeSelector = (timeData, selectedTime, setSelectedTime) => {
  if (!timeData) {
    return <></>;
  }
  const {
    allowedTimes,
    minMorningTime,
    maxMorningTime,
    minAfternoonTime,
    maxAfternoonTime,
    timeStep
  } = timeData;
  return (
    <>
      <div styleName="divisor" />
      <div styleName="time-selector-container">
        <div styleName="time-selector-col">
          <div styleName="time-selector-title">Mattina</div>
          <TimeList
            minTime={minMorningTime}
            maxTime={maxMorningTime}
            allowedTimes={allowedTimes}
            timeStep={timeStep}
            columnNumber={0}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
          />
        </div>
        <div styleName="time-selector-col">
          <div styleName="time-selector-title">Pomeriggio</div>
          <TimeList
            minTime={minAfternoonTime}
            maxTime={maxAfternoonTime}
            allowedTimes={allowedTimes}
            timeStep={timeStep}
            columnNumber={1}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            markOver={
              computeTimeRange(minMorningTime, maxMorningTime, timeStep).length
            }
          />
        </div>
      </div>
    </>
  );
};

SelectDateRange.propTypes = {
  fieldName: PropTypes.string,
  fieldChanged: PropTypes.func.isRequired,
  focusOn: PropTypes.func.isRequired,
  focusedField: PropTypes.string,
  label: PropTypes.string,
  timeData: PropTypes.shape({
    allowedTimes: PropTypes.arrayOf(TimeType).isRequired,
    minMorningTime: TimeType.isRequired,
    maxMorningTime: TimeType.isRequired,
    minAfternoonTime: TimeType.isRequired,
    maxAfternoonTime: TimeType.isRequired,
    timeStep: PropTypes.number.isRequired
  }),
  isDateRange: PropTypes.bool.isRequired,
  date: DateType,
  setDate: PropTypes.func.isRequired,
  time: TimeType,
  setTime: PropTypes.func,
  allowedDateRanges: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.instanceOf(Moment)).isRequired
  ),
  error: PropTypes.string
};

const getDefaultLabel = (isDateRange, hasTime) =>
  (!isDateRange ? "GG/MM/AAAA" : "GG/MM/AAAA - GG/MM/AAAA") +
  (hasTime ? " alle ore HH:MM" : "");

export default connect(
  ({ focusedField }, ownProps) => ({ ...ownProps, focusedField }),
  mapDispatchToProps
)(SelectDateRange);
